import React, { useRef, useState, useCallback, useEffect } from "react";

/**
 * Lightweight before/after slider — drag the handle to reveal the "before" image.
 * No external deps. Works with mouse, touch and keyboard.
 */
export default function BeforeAfterSlider({ before, after, alt = "Before / After cleaning", className = "", beforeLabel = "Before", afterLabel = "After", dim = false }) {
  const [pct, setPct] = useState(50);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    setPct((x / rect.width) * 100);
  }, []);

  const onPointerDown = (e) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e) => { if (draggingRef.current) setFromClientX(e.clientX); };
  const onPointerUp = (e) => {
    draggingRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") setPct((p) => Math.max(0, p - 4));
      else if (e.key === "ArrowRight") setPct((p) => Math.min(100, p + 4));
    };
    const el = containerRef.current;
    el?.addEventListener("keydown", onKey);
    return () => el?.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      role="slider"
      aria-label={alt}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      className={`relative w-full select-none overflow-hidden rounded-3xl border border-[#E5E7EB] shadow-lg cursor-ew-resize focus:outline-none focus:ring-2 focus:ring-[#0EA5A4] ${className}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      data-testid="before-after-slider"
    >
      {/* AFTER (full) */}
      <img src={after} alt={alt} className="block w-full h-full object-cover aspect-[4/3]" draggable={false} />
      {/* BEFORE (clipped) */}
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pct}%` }}>
        <img src={before} alt="Before" className="block h-full object-cover aspect-[4/3]"
          style={{
            width: containerRef.current ? containerRef.current.offsetWidth : "100%",
            maxWidth: "none",
            filter: dim ? "grayscale(0.55) brightness(0.72) contrast(0.92) saturate(0.7)" : "none",
          }}
          draggable={false} />
        {dim && <div className="absolute inset-0 bg-[#1c1917]/15 pointer-events-none" />}
      </div>
      {/* Labels */}
      <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 text-white text-[11px] font-bold uppercase tracking-wider">{beforeLabel}</span>
      <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-[#0EA5A4] text-white text-[11px] font-bold uppercase tracking-wider">{afterLabel}</span>
      {/* Handle */}
      <div className="absolute top-0 bottom-0 w-1 bg-white shadow-lg" style={{ left: `calc(${pct}% - 2px)` }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-xl border-2 border-[#0EA5A4] grid place-items-center">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M5 3L2 7l3 4M9 3l3 4-3 4" stroke="#0EA5A4" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
    </div>
  );
}
